import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  GraduationCap,
  Globe2,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Sparkles,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";

const QUALIFICATIONS = [
  { v: "10th", l: "10th Standard" },
  { v: "12th", l: "12th Standard / Intermediate" },
  { v: "bachelors", l: "Bachelor's degree" },
  { v: "masters", l: "Master's degree" },
];

const CALL_TIMES = [
  { v: "morning", l: "Morning (9 AM – 12 PM)" },
  { v: "afternoon", l: "Afternoon (12 PM – 4 PM)" },
  { v: "evening", l: "Evening (4 PM – 8 PM)" },
];

const COUNTRIES = [
  "USA",
  "UK",
  "Canada",
  "Australia",
  "New Zealand",
  "Germany",
  "Schengen (Europe)",
  "Russia",
  "Georgia",
  "Uzbekistan",
  "Kazakhstan",
  "Kyrgyzstan",
  "Other / Open to recommendation",
];

function yearOptions() {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now + 1; y >= now - 15; y--) years.push(y);
  return years;
}

export default function QuickLeadFormPage() {
  const { slug } = useParams(); // undefined for /lead-form
  const [params] = useSearchParams();
  const tenantSlug = slug || params.get("t") || null;
  const ref = params.get("ref") || null;

  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsapp_number: "",
    whatsapp_same: true,
    email: "",
    qualification: "",
    passing_year: "",
    preferred_country: "",
    best_time_to_call: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const load = async () => {
      try {
        const url = tenantSlug
          ? `${API}/public/branding/${tenantSlug}`
          : null;
        if (!url) {
          setBranding({ tenant_name: "MedAdmits", primary_color: "#0A2540", accent_color: "#C9A961" });
          return;
        }
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setBranding(data);
        } else {
          setBranding({ tenant_name: "MedAdmits", primary_color: "#0A2540", accent_color: "#C9A961" });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantSlug]);

  const primary = branding?.primary_color || "#0A2540";
  const accent = branding?.accent_color || "#C9A961";
  const tenantName = branding?.tenant_name || "MedAdmits";

  const submit = async (e) => {
    e.preventDefault();
    const phoneClean = form.phone.replace(/\D/g, "");
    if (phoneClean.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    const waClean = form.whatsapp_same
      ? phoneClean
      : form.whatsapp_number.replace(/\D/g, "");
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: phoneClean,
        whatsapp_number: waClean || null,
        email: form.email.trim(),
        qualification: form.qualification,
        passing_year: Number(form.passing_year),
        preferred_country: form.preferred_country || null,
        best_time_to_call: form.best_time_to_call,
        tenant_slug: tenantSlug || undefined,
        ref: ref || undefined,
        utm_source: params.get("utm_source") || undefined,
        utm_medium: params.get("utm_medium") || undefined,
        utm_campaign: params.get("utm_campaign") || undefined,
      };
      const res = await fetch(`${API}/leads/quick-register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(
          j.detail
            ? typeof j.detail === "string"
              ? j.detail
              : "Please re-check the form"
            : "Submission failed",
        );
      }
      const data = await res.json();
      setDone(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (done) {
    return (
      <div
        className="flex min-h-screen items-center justify-center p-6"
        style={{ background: `linear-gradient(135deg, ${primary}08, ${accent}10)` }}
      >
        <div
          className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl border border-slate-100"
          data-testid="quick-form-done"
        >
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: `${accent}22` }}
          >
            <CheckCircle2 className="h-7 w-7" style={{ color: accent }} />
          </div>
          <h1
            className="font-display text-2xl font-bold tracking-tight"
            style={{ color: primary }}
          >
            Thank you, {form.name.split(" ")[0]}!
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Our counsellor from <strong>{tenantName}</strong> will reach out to you in
            the {form.best_time_to_call} as you preferred.
          </p>
          <div className="mt-6 rounded-lg bg-slate-50 p-4 text-xs text-slate-500">
            <Sparkles className="mb-1 inline h-3 w-3" style={{ color: accent }} />{" "}
            Save{" "}
            <strong>+91 {form.phone.slice(-10)}</strong> in your contacts so you
            don't miss the call.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-4 sm:p-8"
      style={{ background: `linear-gradient(135deg, ${primary}, ${primary}cc)` }}
      data-testid="quick-form-page"
    >
      <div className="mx-auto max-w-md">
        {/* Header / branding */}
        <div className="mb-6 text-center text-white">
          {branding?.logo_url && (
            <img
              src={branding.logo_url}
              alt={tenantName}
              className="mx-auto mb-3 h-12 rounded-md bg-white p-1"
            />
          )}
          <h1 className="font-display mt-2 text-2xl font-bold leading-tight">
            Book Free Counselling <br /> with our Country Expert
          </h1>
          <p className="mt-2 text-sm opacity-80">
            Talk to a senior counsellor. Personalised university shortlist + a
            scholarship-eligibility check, on us.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-2xl bg-white p-6 shadow-2xl"
        >
          {/* Name */}
          <Field
            id="name"
            label="Full name"
            icon={User}
            input={
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Aarav Sharma"
                data-testid="qf-name"
              />
            }
          />
          {/* Phone */}
          <Field
            id="phone"
            label="Phone number"
            icon={Phone}
            input={
              <Input
                id="phone"
                required
                inputMode="numeric"
                pattern="[0-9+\- ]{7,}"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="98765 43210"
                data-testid="qf-phone"
              />
            }
          />
          {/* WhatsApp number */}
          <div>
            <Label className="text-xs text-slate-500">WhatsApp number</Label>
            <label className="mb-1.5 mt-1 flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={form.whatsapp_same}
                onChange={(e) => set("whatsapp_same", e.target.checked)}
                data-testid="qf-wa-same"
              />
              Same as phone
            </label>
            {!form.whatsapp_same && (
              <Input
                inputMode="numeric"
                value={form.whatsapp_number}
                onChange={(e) => set("whatsapp_number", e.target.value)}
                placeholder="98765 43210"
                data-testid="qf-whatsapp"
                className="mt-1"
              />
            )}
          </div>
          {/* Email */}
          <Field
            id="email"
            label="Email"
            icon={Mail}
            input={
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@email.com"
                data-testid="qf-email"
              />
            }
          />
          {/* Qualification */}
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-1.5">
              <GraduationCap className="h-3.5 w-3.5" />
              Highest qualification
            </Label>
            <Select
              value={form.qualification}
              onValueChange={(v) => set("qualification", v)}
              required
            >
              <SelectTrigger className="mt-1" data-testid="qf-qualification">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {QUALIFICATIONS.map((q) => (
                  <SelectItem key={q.v} value={q.v}>
                    {q.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Passing year */}
          <div>
            <Label className="text-xs text-slate-500">Passing year</Label>
            <Select
              value={form.passing_year}
              onValueChange={(v) => set("passing_year", v)}
              required
            >
              <SelectTrigger className="mt-1" data-testid="qf-passing-year">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {yearOptions().map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Preferred country */}
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-1.5">
              <Globe2 className="h-3.5 w-3.5" />
              Country interested in
            </Label>
            <Select
              value={form.preferred_country}
              onValueChange={(v) => set("preferred_country", v)}
              required
            >
              <SelectTrigger className="mt-1" data-testid="qf-country">
                <SelectValue placeholder="Select destination" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`qf-country-${c}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Best time */}
          <div>
            <Label className="text-xs text-slate-500 flex items-center gap-1.5">
              <MessageCircle className="h-3.5 w-3.5" />
              Best time to call you
            </Label>
            <Select
              value={form.best_time_to_call}
              onValueChange={(v) => set("best_time_to_call", v)}
              required
            >
              <SelectTrigger className="mt-1" data-testid="qf-best-time">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {CALL_TIMES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>
                    {t.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-2 h-12 w-full text-base font-semibold"
            style={{ background: primary, color: "white" }}
            data-testid="qf-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
              </>
            ) : (
              "Talk to a counsellor"
            )}
          </Button>

          <p className="text-center text-[10px] text-slate-400">
            By submitting, you agree to be contacted by {tenantName} via call &
            WhatsApp.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ id, label, icon: Icon, input }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-slate-500 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </Label>
      <div className="mt-1">{input}</div>
    </div>
  );
}
